import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Zap, Info, Moon, Sun, X, Percent, Maximize2, Lock, Unlock, Plus } from "lucide-react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import heic2any from "heic2any";
import ToolsNav from "../components/ToolsNav";
import { RenderCMSContent } from "../components/CMSRenderer";
import { AdContainer, ResponsiveAd, StickyMobileAd, DesktopOnlyAd } from "../components/AdPlacement";

function ResizeByPercentage({ embedded = false }) {
  const [images, setImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [resizeProgress, setResizeProgress] = useState(0);
  const [resizeMode, setResizeMode] = useState("percentage"); // "percentage" or "custom"
  const [percentage, setPercentage] = useState("50");
  const [customWidth, setCustomWidth] = useState("");
  const [customHeight, setCustomHeight] = useState("");
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [originalDimensions, setOriginalDimensions] = useState({ width: 0, height: 0 });
  const [outputFormat, setOutputFormat] = useState("original"); // "original", "png", "jpg", "jpeg"
  const [pageContent, setPageContent] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Resize Image by Percentage - KayTool";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Resize images by percentage (25%, 50%, 75%) or custom dimensions. Free online tool with instant preview.');
  }, []);

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pages/slug/resize-by-percentage`);
        if (response.ok) {
          const data = await response.json();
          setPageContent(data);
        }
      } catch (error) {
        console.log("No CMS content found");
      }
    };
    fetchPageContent();
  }, [API_URL]);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    await processFiles(files);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await processFiles(files);
    }
  };

  const processFiles = async (files) => {
    const imageFiles = files.filter(f => f.type.startsWith('image/') || f.name.toLowerCase().endsWith('.heic') || f.name.toLowerCase().endsWith('.heif'));
    
    if (imageFiles.length === 0) {
      toast.error("Please select valid image files");
      return;
    }

    setIsProcessing(true);
    setResizeProgress(0);

    const newImages = [];

    for (let i = 0; i < imageFiles.length; i++) {
      let file = imageFiles[i];
      
      // Convert HEIC/HEIF to JPEG
      if (file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')) {
        try {
          const convertedBlob = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.95 });
          file = new File([convertedBlob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
        } catch (error) {
          toast.error(`Failed to convert ${file.name}`);
          continue;
        }
      }

      // Get original dimensions
      const dimensions = await getImageDimensions(file);
      if (i === 0) {
        setOriginalDimensions(dimensions);
        setCustomWidth(dimensions.width.toString());
        setCustomHeight(dimensions.height.toString());
      }

      newImages.push({
        id: Date.now() + i,
        name: file.name,
        file: file,
        preview: URL.createObjectURL(file),
        originalWidth: dimensions.width,
        originalHeight: dimensions.height,
        originalSize: file.size,
        resized: null,
        resizedWidth: null,
        resizedHeight: null,
        resizedSize: null,
      });

      setResizeProgress(Math.round(((i + 1) / imageFiles.length) * 100));
    }

    setImages(prev => [...prev, ...newImages]);
    setIsProcessing(false);
    toast.success(`Added ${newImages.length} image${newImages.length > 1 ? 's' : ''}`);
  };

  const getImageDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(img.src);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const resizeImage = async (file, targetWidth, targetHeight) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
        
        // Determine output format and MIME type
        let mimeType = file.type || 'image/jpeg';
        let quality = 0.92;
        
        if (outputFormat !== 'original') {
          if (outputFormat === 'png') {
            mimeType = 'image/png';
            quality = 1.0; // PNG doesn't use quality
          } else if (outputFormat === 'jpg' || outputFormat === 'jpeg') {
            mimeType = 'image/jpeg';
          }
        }
        
        canvas.toBlob((blob) => {
          resolve({ blob, mimeType });
        }, mimeType, quality);
        
        URL.revokeObjectURL(img.src);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleResize = async () => {
    if (images.length === 0) {
      toast.error("Please add images first");
      return;
    }

    setIsProcessing(true);
    setResizeProgress(0);

    const updatedImages = [];

    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      let targetWidth, targetHeight;

      if (resizeMode === "percentage") {
        const scale = parseInt(percentage) / 100;
        targetWidth = Math.round(image.originalWidth * scale);
        targetHeight = Math.round(image.originalHeight * scale);
      } else {
        targetWidth = parseInt(customWidth) || image.originalWidth;
        targetHeight = parseInt(customHeight) || image.originalHeight;
      }

      try {
        const { blob: resizedBlob, mimeType } = await resizeImage(image.file, targetWidth, targetHeight);
        
        // Determine file extension based on output format
        let newExtension = '';
        if (outputFormat === 'png') newExtension = '.png';
        else if (outputFormat === 'jpg') newExtension = '.jpg';
        else if (outputFormat === 'jpeg') newExtension = '.jpeg';
        
        updatedImages.push({
          ...image,
          resized: resizedBlob,
          resizedWidth: targetWidth,
          resizedHeight: targetHeight,
          resizedSize: resizedBlob.size,
          resizedPreview: URL.createObjectURL(resizedBlob),
          outputFormat: outputFormat,
          newExtension: newExtension,
          mimeType: mimeType,
        });
      } catch (error) {
        toast.error(`Failed to resize ${image.name}`);
        updatedImages.push(image);
      }

      setResizeProgress(Math.round(((i + 1) / images.length) * 100));
    }

    setImages(updatedImages);
    setIsProcessing(false);
    toast.success("Images resized successfully!");
  };

  const handleWidthChange = (value) => {
    setCustomWidth(value);
    if (maintainAspectRatio && originalDimensions.width > 0) {
      const ratio = originalDimensions.height / originalDimensions.width;
      setCustomHeight(Math.round(parseInt(value) * ratio).toString());
    }
  };

  const handleHeightChange = (value) => {
    setCustomHeight(value);
    if (maintainAspectRatio && originalDimensions.height > 0) {
      const ratio = originalDimensions.width / originalDimensions.height;
      setCustomWidth(Math.round(parseInt(value) * ratio).toString());
    }
  };

  const downloadSingle = (image) => {
    if (!image.resized) return;
    // Use new extension if format was changed, otherwise use original
    let ext = image.newExtension ? image.newExtension.replace('.', '') : (image.file.type === 'image/png' ? 'png' : 'jpg');
    const newName = image.name.replace(/\.[^/.]+$/, `_resized.${ext}`);
    saveAs(image.resized, newName);
  };

  const downloadAll = async () => {
    const resizedImages = images.filter(img => img.resized);
    if (resizedImages.length === 0) {
      toast.error("No resized images to download");
      return;
    }

    if (resizedImages.length === 1) {
      downloadSingle(resizedImages[0]);
      return;
    }

    const zip = new JSZip();
    resizedImages.forEach((image, index) => {
      let ext = image.newExtension ? image.newExtension.replace('.', '') : (image.file.type === 'image/png' ? 'png' : 'jpg');
      const newName = image.name.replace(/\.[^/.]+$/, `_resized.${ext}`);
      zip.file(newName, image.resized);
    });

    const zipBlob = await zip.generateAsync({ type: "blob" });
    saveAs(zipBlob, "resized_images.zip");
  };

  const removeImage = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  const clearAll = () => {
    setImages([]);
    setOriginalDimensions({ width: 0, height: 0 });
    setCustomWidth("");
    setCustomHeight("");
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAddMore = (e) => {
    const files = e.target.files;
    if (files) {
      handleFileSelect({ target: { files } });
    }
    e.target.value = '';
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-slate-50 to-blue-50'}`}>
      <Toaster position="top-center" richColors />
      
      {!embedded && (
        <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <a href="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                <Percent className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
            </a>
            <div className="flex items-center gap-4">
              <ToolsNav />
              <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
                <Info className="w-3 h-3" /> 100% Local Processing
              </Badge>
              <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </header>
      )}

      {/* Ad Placement - Above the Fold */}
      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Resize Image by Percentage
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Resize your images by percentage (25%, 50%, 75%) or set custom dimensions. 
            All processing happens locally in your browser.
          </p>
        </div>

        {/* Settings Card */}
        <Card className="mb-6 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-6">
            <div className="space-y-6">
              {/* Resize Mode */}
              <div>
                <Label className="text-base font-semibold text-slate-800 dark:text-white mb-3 block">
                  Resize Mode
                </Label>
                <RadioGroup value={resizeMode} onValueChange={setResizeMode} className="flex flex-wrap gap-4">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="percentage" id="percentage" />
                    <Label htmlFor="percentage" className="cursor-pointer">By Percentage</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="custom" id="custom" />
                    <Label htmlFor="custom" className="cursor-pointer">Custom Dimensions</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Percentage Options */}
              {resizeMode === "percentage" && (
                <div>
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 block">
                    Select Percentage
                  </Label>
                  <div className="flex flex-wrap gap-3">
                    {["25", "50", "75"].map((p) => (
                      <Button
                        key={p}
                        variant={percentage === p ? "default" : "outline"}
                        onClick={() => setPercentage(p)}
                        className={percentage === p ? "bg-purple-600 hover:bg-purple-700" : ""}
                      >
                        {p}%
                      </Button>
                    ))}
                  </div>
                  {images.length > 0 && (
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                      {images[0].originalWidth} × {images[0].originalHeight} → {Math.round(images[0].originalWidth * parseInt(percentage) / 100)} × {Math.round(images[0].originalHeight * parseInt(percentage) / 100)}
                    </p>
                  )}
                </div>
              )}

              {/* Custom Dimensions */}
              {resizeMode === "custom" && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setMaintainAspectRatio(!maintainAspectRatio)}
                      className="gap-2"
                    >
                      {maintainAspectRatio ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                      {maintainAspectRatio ? "Aspect Ratio Locked" : "Aspect Ratio Unlocked"}
                    </Button>
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="width" className="text-sm">Width:</Label>
                      <Input
                        id="width"
                        type="number"
                        value={customWidth}
                        onChange={(e) => handleWidthChange(e.target.value)}
                        className="w-24"
                        placeholder="Width"
                      />
                      <span className="text-sm text-slate-500">px</span>
                    </div>
                    <span className="text-slate-400">×</span>
                    <div className="flex items-center gap-2">
                      <Label htmlFor="height" className="text-sm">Height:</Label>
                      <Input
                        id="height"
                        type="number"
                        value={customHeight}
                        onChange={(e) => handleHeightChange(e.target.value)}
                        className="w-24"
                        placeholder="Height"
                      />
                      <span className="text-sm text-slate-500">px</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Output Format Selection */}
              <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 block">
                  Output Format
                </Label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: "original", label: "Original" },
                    { value: "png", label: "PNG" },
                    { value: "jpg", label: "JPG" },
                    { value: "jpeg", label: "JPEG" },
                  ].map((format) => (
                    <Button
                      key={format.value}
                      variant={outputFormat === format.value ? "default" : "outline"}
                      size="sm"
                      onClick={() => setOutputFormat(format.value)}
                      className={outputFormat === format.value ? "bg-indigo-600 hover:bg-indigo-700" : ""}
                      data-testid={`format-${format.value}`}
                    >
                      {format.label}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                  {outputFormat === "original" ? "Keep the original image format" : `Convert all images to ${outputFormat.toUpperCase()}`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upload Area */}
        <Card 
          className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 ${
            isDragging ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'border-slate-300 dark:border-slate-600'
          }`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <CardContent className="p-12 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.heic,.heif"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            <div 
              className="cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-16 h-16 mx-auto mb-4 text-purple-500" />
              <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                Drop, paste, or click to upload
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-4">
                Supports JPEG, PNG, WebP, GIF, HEIC
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Processing Progress */}
        {isProcessing && (
          <Card className="mt-6 dark:bg-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Progress value={resizeProgress} className="h-3" />
                </div>
                <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                  {resizeProgress}%
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Images List */}
        {images.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                Images ({images.length})
              </h3>
              <div className="flex gap-2 flex-wrap">
                <input
                  ref={(el) => { if (el) el.id = 'addMoreInputPerc'; }}
                  type="file"
                  accept="image/*,.heic"
                  multiple
                  onChange={handleAddMore}
                  className="hidden"
                  id="addMoreInputPerc"
                />
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => document.getElementById('addMoreInputPerc')?.click()}
                  className="text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add More
                </Button>
                <Button variant="outline" size="sm" onClick={clearAll} className="text-red-600">
                  <X className="w-4 h-4 mr-1" /> Clear All
                </Button>
                <Button 
                  onClick={handleResize} 
                  disabled={isProcessing}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Maximize2 className="w-4 h-4 mr-2" />
                  Resize {images.length > 1 ? 'All' : 'Image'}
                </Button>
              </div>
            </div>

            {/* Ad Placement - Above Results */}
            <DesktopOnlyAd slot="above-results" size="large-rectangle" className="my-4" />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {images.map((image) => (
                <Card key={image.id} className="dark:bg-slate-800 overflow-hidden">
                  <div className="aspect-video relative bg-slate-100 dark:bg-slate-700">
                    <img
                      src={image.resizedPreview || image.preview}
                      alt={image.name}
                      className="w-full h-full object-contain"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 w-8 h-8"
                      onClick={() => removeImage(image.id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                    {image.resized && (
                      <Badge className="absolute top-2 left-2 bg-green-500">Resized</Badge>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <p className="font-medium text-slate-800 dark:text-white truncate mb-2">{image.name}</p>
                    <div className="text-sm text-slate-500 dark:text-slate-400 space-y-1">
                      <p>Original: {image.originalWidth} × {image.originalHeight} ({formatSize(image.originalSize)})</p>
                      {image.resized && (
                        <p className="text-green-600 dark:text-green-400">
                          Resized: {image.resizedWidth} × {image.resizedHeight} ({formatSize(image.resizedSize)})
                        </p>
                      )}
                    </div>
                    {image.resized && (
                      <Button
                        size="sm"
                        className="w-full mt-3 bg-purple-600 hover:bg-purple-700"
                        onClick={() => downloadSingle(image)}
                      >
                        <Download className="w-4 h-4 mr-2" /> Download
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {images.some(img => img.resized) && images.length > 1 && (
              <div className="mt-4 text-center">
                <Button onClick={downloadAll} size="lg" className="bg-purple-600 hover:bg-purple-700">
                  <Download className="w-5 h-5 mr-2" /> Download All (ZIP)
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Ad Placement - Before Features */}
        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>

        {/* Features */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[
            { icon: Percent, title: "Percentage Resize", desc: "Quickly resize to 25%, 50%, or 75% of original" },
            { icon: Maximize2, title: "Custom Dimensions", desc: "Set exact width and height in pixels" },
            { icon: Info, title: "100% Private", desc: "All processing happens in your browser" }
          ].map((feature, i) => (
            <Card key={i} className="dark:bg-slate-800">
              <CardContent className="p-6 text-center">
                <feature.icon className="w-10 h-10 mx-auto mb-3 text-purple-500" />
                <h4 className="font-semibold mb-2 dark:text-white">{feature.title}</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">{feature.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>

      {pageContent?.content && (
        <div className="bg-slate-50 dark:bg-slate-800/50 py-12">
          <div className="container mx-auto px-4">
            <RenderCMSContent content={pageContent.content} />
          </div>
        </div>
      )}

      {/* Ad Placement - Before Footer */}
      <AdContainer className="my-6">
        <ResponsiveAd slot="before-footer" mobileSize="mobile-rectangle" desktopSize="rectangle" />
      </AdContainer>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      {/* Sticky Mobile Ad */}
      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default ResizeByPercentage;
